import React, { useState, useEffect } from 'react';
import { api } from '../api';

const STATUT_COLORS = {
  draft: '#94a3b8', brouillon: '#94a3b8',
  envoyée: '#60a5fa', sent: '#60a5fa',
  'acceptée': '#34d399', accepted: '#34d399',
  payée: '#34d399', paid: '#34d399',
  facturée: '#a78bfa',
  'annulée': '#f87171', cancelled: '#f87171',
  pending: '#fbbf24',
};

const Badge = ({ status }) => (
  <span style={{
    padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: '600',
    background: (STATUT_COLORS[status] || '#94a3b8') + '22',
    color: STATUT_COLORS[status] || '#94a3b8',
  }}>{status}</span>
);

const fmtMoney = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

export default function ClientPortal() {
  const token = window.location.pathname.split('/portail/')[1];
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [onglet, setOnglet] = useState('factures');

  useEffect(() => {
    if (!token) { setError('Lien invalide'); return; }
    api.getPublicPortail(token)
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Erreur de chargement'));
  }, [token]);

  const s = {
    page: { minHeight: '100vh', background: '#f0f4f8', fontFamily: 'system-ui,sans-serif' },
    header: { background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', color: 'white', padding: '2rem 1.5rem' },
    container: { maxWidth: '800px', margin: '0 auto', padding: '0 1rem' },
    card: { background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: '1.5rem', marginBottom: '1rem' },
    tab: (active) => ({
      padding: '0.6rem 1.2rem', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', border: 'none',
      background: active ? '#2563eb' : 'white', color: active ? 'white' : '#64748b',
      boxShadow: active ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
    }),
    th: { padding: '0.6rem 0.75rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.8rem', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '0.75rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem' },
  };

  if (error) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', padding: '3rem', borderRadius: '1rem', textAlign: 'center', maxWidth: '400px' }}>
        <div style={{ fontSize: '3rem' }}>❌</div>
        <h2 style={{ color: '#0f172a' }}>{error}</h2>
        <p style={{ color: '#64748b' }}>Ce lien est invalide ou a expiré.</p>
      </div>
    </div>
  );

  if (!data) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
      Chargement...
    </div>
  );

  const { artisan, client, invoices = [], quotes = [] } = data;
  const totalFactures = invoices.reduce((s, i) => s + Number(i.amount_ttc || i.amount || 0), 0);
  const totalDevis    = quotes.reduce((s, q) => s + Number(q.amount || 0), 0);
  const nbPayees      = invoices.filter(i => i.status === 'payée').length;

  return (
    <div style={s.page}>
      {/* Header artisan */}
      <div style={s.header}>
        <div style={s.container}>
          <div style={{ fontSize: '0.85rem', opacity: 0.75, marginBottom: '0.4rem' }}>Espace client de</div>
          <h1 style={{ margin: 0, fontSize: '1.6rem' }}>{artisan.nomEntreprise || artisan.name}</h1>
          {artisan.siret && <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.2rem' }}>SIRET {artisan.siret}</div>}
          {artisan.email && <div style={{ fontSize: '0.85rem', opacity: 0.8, marginTop: '0.2rem' }}>✉ {artisan.email}{artisan.phone ? ` · 📞 ${artisan.phone}` : ''}</div>}
          {client?.name && <div style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.15)', borderRadius: '0.5rem', display: 'inline-block', fontSize: '0.9rem' }}>👤 {client.name}</div>}
        </div>
      </div>

      <div style={{ ...s.container, paddingTop: '1.5rem' }}>
        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Factures', value: invoices.length, sub: fmtMoney(totalFactures) },
            { label: 'Payées', value: nbPayees, sub: `${invoices.length ? Math.round(nbPayees/invoices.length*100) : 0}%` },
            { label: 'Devis', value: quotes.length, sub: fmtMoney(totalDevis) },
          ].map(kpi => (
            <div key={kpi.label} style={{ ...s.card, textAlign: 'center', padding: '1.25rem' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0f172a' }}>{kpi.value}</div>
              <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600' }}>{kpi.label}</div>
              <div style={{ color: '#2563eb', fontSize: '0.8rem', marginTop: '0.2rem' }}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* Onglets */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button style={s.tab(onglet === 'factures')} onClick={() => setOnglet('factures')}>📄 Factures ({invoices.length})</button>
          <button style={s.tab(onglet === 'devis')}    onClick={() => setOnglet('devis')}>💰 Devis ({quotes.length})</button>
        </div>

        {/* Factures */}
        {onglet === 'factures' && (
          <div style={s.card}>
            {invoices.length === 0
              ? <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Aucune facture</p>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={s.th}>N°</th>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Échéance</th>
                      <th style={s.th}>Montant TTC</th>
                      <th style={s.th}>Statut</th>
                      <th style={s.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id}>
                        <td style={{ ...s.td, fontWeight: '700', color: '#2563eb' }}>{inv.number}</td>
                        <td style={s.td}>{fmtDate(inv.created_at)}</td>
                        <td style={s.td}>{fmtDate(inv.due_date)}</td>
                        <td style={{ ...s.td, fontWeight: '700' }}>{fmtMoney(inv.amount_ttc || inv.amount)}</td>
                        <td style={s.td}><Badge status={inv.status} /></td>
                        <td style={s.td}>
                          {inv.status !== 'payée' && inv.payment_link_url && (
                            <a href={inv.payment_link_url} target="_blank" rel="noreferrer"
                              style={{ background: '#2563eb', color: 'white', padding: '0.3rem 0.7rem', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: '600', textDecoration: 'none' }}>
                              💳 Payer
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        )}

        {/* Devis */}
        {onglet === 'devis' && (
          <div style={s.card}>
            {quotes.length === 0
              ? <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Aucun devis</p>
              : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={s.th}>N°</th>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Expiration</th>
                      <th style={s.th}>Montant</th>
                      <th style={s.th}>Statut</th>
                      <th style={s.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map(qt => (
                      <tr key={qt.id}>
                        <td style={{ ...s.td, fontWeight: '700', color: '#7c3aed' }}>{qt.number}</td>
                        <td style={s.td}>{fmtDate(qt.created_at)}</td>
                        <td style={s.td}>{fmtDate(qt.expiry_date)}</td>
                        <td style={{ ...s.td, fontWeight: '700' }}>{fmtMoney(qt.amount)}</td>
                        <td style={s.td}><Badge status={qt.status} /></td>
                        <td style={s.td}>
                          {qt.share_token && !qt.signed_at && qt.status !== 'acceptée' && (
                            <a href={`/sign/${qt.share_token}`}
                              style={{ background: '#059669', color: 'white', padding: '0.3rem 0.7rem', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: '600', textDecoration: 'none' }}>
                              🖊 Signer
                            </a>
                          )}
                          {qt.signed_at && <span style={{ color: '#059669', fontSize: '0.82rem', fontWeight: '600' }}>✅ Signé</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        )}

        <p style={{ color: '#94a3b8', fontSize: '0.78rem', textAlign: 'center', marginTop: '2rem' }}>
          Espace client sécurisé — {artisan.nomEntreprise || artisan.name} · Propulsé par FacturArt
        </p>
      </div>
    </div>
  );
}
